import animateScrollTo from 'animated-scroll-to'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { addClasses, addClassesForNFrames, queryOne, removeClasses } from 'lambda-dom'
import QueryString from 'qs'
import { not, prop } from 'ramda'
import { concatMap, map, Subject, timer } from 'rxjs'

import { axios, BreakPoint, getWindowBreakPointForNewsroom, youtubeApiP } from '@app-bootstrappers'
import { ScrollBlockers } from '@app-helpers'
import { autoSearch, current, replace } from '@app-services'
import {
    allNewsroomData,
    anyQuestionsCard,
    eventsCard,
    filterBar,
    filterContainer,
    filtersCard,
    noResultsHeader,
    noResultsSearchField,
    noResultsSearchForm,
    searchForm,
} from './newsroom-overview.bootstrap'
import {
    ArticleDataInterface,
    ArticleFilter,
    ArticleSectionDataInterface,
    DisplayType,
    QueryKey,
    QueryType,
    QuickFilter,
    SpecialCardPositions,
    videoComponentTemplate,
    VideoData,
} from './newsroom-overview.types'
import { getCachedArticles, getCachedSections, setCachedArticles, setCachedSections } from './newsroom-overview.store'


gsap.registerPlugin(ScrollTrigger)

async function updateArticleClap(articleId: string): Promise<any> {
    return axios.post('/progress/article-votes', { articleId })
        .then(prop('data'))
}

export function handleArticleClaps(article: HTMLElement): void {
    const clap = article.querySelector('.article-card__clap')
    const counter = article.querySelector('.article-card__counter')
    const id = article.dataset.preview

    if (! clap
        || ! (clap instanceof HTMLDivElement)
        || ! counter
        || ! (counter instanceof HTMLParagraphElement)
        || ! id
    ) return

    let upvoteCountStorage = counter.innerHTML
    let animationInProgress = false

    clap.addEventListener('click', () => {
        if (animationInProgress) return

        updateArticleClap(id).then((response) => {
            if (response.upvoteCount) {
                counter.classList.remove('hover-state')
                counter.classList.add('clicked')
                animationInProgress = true

                setTimeout(() => {
                    counter.classList.remove('clicked')
                    counter.innerHTML = response.upvoteCount
                    upvoteCountStorage = counter.innerHTML
                    animationInProgress = false
                }, 1000)
            }
        })
    })

    clap.addEventListener('mouseover', () => {
        if (animationInProgress) return

        counter.classList.add('hover-state')
        counter.innerHTML = '+1'
    })

    clap.addEventListener('mouseout', () => {
        if (animationInProgress) return

        counter.classList.remove('hover-state')
        counter.innerHTML = upvoteCountStorage
    })

    clap.addEventListener('mouseleave', () => {
        if (animationInProgress) return

        counter.classList.remove('hover-state')
        counter.innerHTML = upvoteCountStorage
    })
}

export const readUrlFilter = (): ArticleFilter | null => {
    const queryObject = current.queryObject()

    return isArticleFilter(queryObject) ? cleanFilterObject(queryObject) : null
}

/**
 * Transforms the given ParsedQs object into a usable ArticleFilter.
 */
export const cleanFilterObject = (object: QueryString.ParsedQs): ArticleFilter => <ArticleFilter>{
    [QueryKey.QUERY_TYPE]: Object.keys(object)[Object.keys(object).findIndex((key) => ! key.startsWith('_'))],
    [QueryKey.QUERY_VALUE]: Object.values(object)[Object.keys(object).findIndex((key) => ! key.startsWith('_'))],
}

/**
 * This runtime test decides if given values extends the ArticleFilter interface.
 */
export const isArticleFilter = (query: QueryString.ParsedQs): boolean =>
    (query.hasOwnProperty(QueryKey.QUICK_FILTER) && query[QueryKey.QUICK_FILTER] !== '')
    || (query.hasOwnProperty(QueryKey.TAG) && query[QueryKey.TAG] !== '')
    || (query.hasOwnProperty(QueryKey.SEARCH_TERM) && query[QueryKey.SEARCH_TERM] !== '')

// ------------------------------------------------------------------------------
//      Filter UI
// ------------------------------------------------------------------------------

/**
 * Show the filter menu overlay.
 */
export const showFilters = () => {
    filterContainer.classList.add('active')
    ScrollBlockers.add('newsroom-filters')
}

/**
 * Hide the filter menu overlay.
 */
export const hideFilters = () => {
    filterContainer.classList.remove('active')
    ScrollBlockers.remove('newsroom-filters')
}
/**
 * Show the filter state bar.
 */
export const showFilterBar = (transition: boolean = true) => {
    if (not(transition)) {
        addClassesForNFrames(1, 'no-transition')(filterBar)
    }
    addClasses('active')(filterBar)
}

/**
 * Hide the filter state bar.
 */
export const hideFilterBar = (transition: boolean = true) => {
    if (not(transition)) {
        addClassesForNFrames(1, 'no-transition')(filterBar)
    }
    removeClasses('active')(filterBar)
}

/**
 * Read the filter state from the search-field. Returns a 'search filter' if the field is
 * not empty, or `null` otherwise.
 */
export const readSearchForm = (): ArticleFilter | null => {
    const filter = {
        [QueryKey.QUERY_TYPE]: QueryType.SEARCH_QUERY,
        [QueryKey.QUERY_VALUE]: (new FormData(searchForm)).get(QueryKey.QUERY_VALUE) as string,
    }

    return isArticleFilter(filter) ? filter : null
}

/**
 * Read the filter state from the no results search-field. Returns a 'search filter' if the field is
 * not empty, or `null` otherwise.
 */
export const readNoResultsSearchForm = (): ArticleFilter | null => {
    const filter = {
        [QueryKey.QUERY_TYPE]: QueryType.SEARCH_QUERY,
        [QueryKey.QUERY_VALUE]: (new FormData(noResultsSearchForm)).get(QueryKey.QUERY_VALUE) as string,
    }

    return isArticleFilter(filter) ? filter : null
}

/**
 * Attempts to JSON parse the given serialized article-filter (or undefined). Returns undefined if the
 * input is undefined, if parsing fails, or if the parsed object is not a valid filter.
 */
export const parseSerializedFilter = (serialized: string | undefined): ArticleFilter | undefined => {
    try {
        const maybeFilter = JSON.parse(serialized!)
        return isArticleFilter(maybeFilter) ? cleanFilterObject(maybeFilter) : undefined
    } catch (e) {
        return undefined
    }
}

/**
 * Sets the given article-filter in the browser URL. When `null`, any existing filter
 * parameters are removed from the current querystring.
 */
export const setUrlFilter = (filter: ArticleFilter | null) => {
    if (filter === null || filter[QueryKey.QUERY_VALUE] === QuickFilter.ALL) {
        const params = current.queryObject()
        delete params[QueryKey.QUERY_TYPE]
        delete params[QueryKey.QUERY_VALUE]
        replace.setQueryParams(params)
    } else {
        // HistoryService.replace.patchQueryParams(cleanFilterObject(filter))
    }
}

const makeElementFromString = (string: string) => {
    let returnValue = document.createElement('div')

    returnValue.innerHTML = string.trim()

    return returnValue.firstChild!
}

const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

/**
 * Builds an HTML string for the given article data.
 */
export const articleHtml = (article: ArticleDataInterface): string => {
    const template = document.getElementById('article-card-template')

    if (! template || ! (template instanceof HTMLTemplateElement)) return ''

    const articleTemplate = template.content.querySelector('.article-card')

    if (! articleTemplate) return ''

    const articleElement = articleTemplate.cloneNode(true) as HTMLElement

    const articleImage = articleElement.querySelector('.article-card__image')
    const articleDate = articleElement.querySelector('.article-card__date')
    const articleTitle = articleElement.querySelector('.article-card__title')
    const articleHeadshot = articleElement.querySelector('.article-card__headshot')
    const articleEmployeeName = articleElement.querySelector('.article-card__author-name')
    const articleClapCounter = articleElement.querySelector('.article-card__counter')

    articleElement.dataset.preview = article.id.toString()

    articleElement.classList.add(article.displayTypeSlug)

    if (! article.hasImage && ! article.youtubeVideoId) articleElement.classList.add('no-media')

    if (article.youtubeVideoId) {
        articleElement.classList.add('video')
        articleElement.dataset.videoId = article.youtubeVideoId
    }

    const imageUrl = article.imageUrl

    if (imageUrl && articleImage && articleImage instanceof HTMLImageElement) {
        articleImage.srcset = `${imageUrl} 176w, ${imageUrl} 300w`
        articleImage.sizes = '(max-width: 992px) 176px, 300px'
        articleImage.alt = article.imageCaption ?? ''
        articleImage.src = imageUrl
    }

    if (articleDate && articleDate instanceof HTMLDivElement) {
        let date = new Date('01-01-1970')

        if (typeof article.publicationDate === 'string') {
            date = new Date(article.publicationDate.replace(/-/g, '/'))

            if (isNaN(date.getTime())) date = new Date(article.publicationDate)
        }

        if (typeof article.publicationDate === 'object' && article.publicationDate.date) {
            date = new Date(article.publicationDate.date.replace(/-/g, '/'))

            if (isNaN(date.getTime())) date = new Date(article.publicationDate.date)
        }

        const year = date.getFullYear()
        const month = months[date.getMonth()]
        const day = date.getDate()

        articleDate.innerText = `${day} ${month} ${year}`
    }

    if (articleTitle && articleTitle instanceof HTMLDivElement) {
        articleTitle.innerText = article.title
    }

    if (article.contactPerson && article.contactPersonAvatar && articleHeadshot && articleHeadshot instanceof HTMLImageElement) {
        articleHeadshot.src = article.contactPersonAvatar
        articleHeadshot.alt = `portrait for ${article.contactPerson.name}`
    }

    if (article.contactPerson && articleEmployeeName && articleEmployeeName instanceof HTMLParagraphElement) {
        articleEmployeeName.innerText = article.contactPerson.name
    }

    if (articleClapCounter && articleClapCounter instanceof HTMLParagraphElement) {
        articleClapCounter.innerText = article.upvoteCount ? article.upvoteCount.toString() : '0'
    }

    if (articleTitle
        && (article.displayTitleOverImage)
        && ! (articleElement.classList.contains('no-media'))
    ) {
        const clonedTitle = articleTitle.cloneNode(true)

        const articleMedia = articleElement.querySelector('.article-card__media')

        if (articleMedia) {
            articleMedia.appendChild(clonedTitle)

            articleTitle.remove()

            articleElement.classList.add('alternate-title')
        }

        if (article.displayType === DisplayType.NEWS_ARTICLE || article.displayType === DisplayType.THOUGHT_LEADER) {
            articleElement.classList.add('hide-content')
        }

        if (article.displayType === DisplayType.NEWS_ARTICLE) {
            articleElement.classList.add('full-border-radius')
        }
    }

    return articleElement.outerHTML
}

export const articleSectionHtml = (section: ArticleSectionDataInterface, nextList: number): string => {
    // TODO: not finished. only heading and dataset is added at the moment

    const template = document.getElementById('article-section-template')

    if (! template || ! (template instanceof HTMLTemplateElement)) return ''

    const sectionTemplate = template.content.querySelector('.article-section')

    if (! sectionTemplate) return ''

    const sectionElement = sectionTemplate.cloneNode(true) as HTMLElement

    sectionElement.dataset.nextList = nextList.toString()

    const sectionHeader = sectionElement.querySelector('.article-section__heading')

    if (sectionHeader && sectionHeader instanceof HTMLHeadingElement) {
        sectionHeader.innerHTML = section.heading
    }

    return sectionElement.outerHTML
}

const placeArticleLanes = (breakPoint: number, articlesContainer: HTMLDivElement, listIndex: number) => {
    const articlesList = document.createElement('div')

    articlesList.classList.add('newsroom-article-list', 'nal')
    articlesList.dataset.listIndex = listIndex.toString()

    let elements: HTMLDivElement[] = []

    for (let i = 1; i <= breakPoint; i++) {
        const element = document.createElement('div')

        element.setAttribute('class', 'newsroom-article-grid')
        element.setAttribute('id', `newsroom-articles-list-${i}`)

        articlesList.appendChild(element)
        elements.push(element)
    }

    articlesContainer.appendChild(articlesList)

    return elements
}

const placeArticleSection = (section: ArticleSectionDataInterface, articlesContainer: HTMLDivElement, nextList: number) => {
    const element = makeElementFromString(articleSectionHtml(section, nextList)) as HTMLElement

    articlesContainer.appendChild(element)

    const observer = new IntersectionObserver(sectionObserverCallback, { threshold: [1, 0.85, 0] })

    observer.observe(element)

    function sectionObserverCallback(entries: IntersectionObserverEntry[]) {
        entries.forEach((entry) => {
            if (entry.intersectionRatio > 0.85) {
                const nextIdentifier = element.dataset.nextList

                if (nextIdentifier) {
                    const nextListElement = document.querySelector(`[data-list-index="${nextIdentifier}"]`)
                    const nextSection = document.querySelector(`[data-next-list="${parseInt(nextIdentifier) + 1}"]`)

                    if (nextListElement && nextListElement instanceof HTMLDivElement) initiateArticleListAnimations(nextListElement)
                    if (nextSection && nextSection instanceof HTMLElement) initiateArticleSectionAnimation(nextSection)
                }

                observer.disconnect()
            }
        })
    }
}

const videoQueue = new Subject<VideoData>()
const isFirstVideo = { value: true }

videoQueue.pipe(
    concatMap(videoData =>
        timer(isFirstVideo.value ? 0 : 1000).pipe(
            map(() => {
                isFirstVideo.value = false
                return videoData
            }),
        ),
    ),
).subscribe(videoData => {
    if (videoData.shouldPlay) {
        videoData.iframe.style.opacity = '1'
        videoData.target.playVideo()
    } else {
        videoData.iframe.style.opacity = '0'
        videoData.target.stopVideo()
    }
})

const initiateArticleVideo = (article: HTMLElement): void => {
    const mediaContainer = article.querySelector('.article-card__media')
    const videoId = article.dataset.videoId

    if (! mediaContainer || ! videoId) return

    mediaContainer.appendChild(makeElementFromString(videoComponentTemplate(videoId)))

    youtubeApiP.then(() => {
        new YT.Player(`overview-youtube-video-${videoId}`, {
            events: {
                'onReady': onPlayerReady,
            },
            playerVars: {
                autohide: 1,
                enablejsapi: 1,
                showinfo: 0,
                controls: 0,
                fs: 0,
                modestbranding: 1,
                loop: 1,
                playlist: videoId,
            },
        })

        function onPlayerReady(event: any) {
            event.target.mute()
            let activated = false

            const observer = new IntersectionObserver(
                ([element]) => {
                    const shouldActivate = element.intersectionRatio >= 0.85
                    const iframe = article.querySelector('iframe')!

                    const videoData: VideoData = {
                        iframe,
                        target: event.target,
                        shouldPlay: shouldActivate,
                    }

                    if (shouldActivate == activated) return

                    activated = shouldActivate

                    videoQueue.next(videoData)
                },
                { threshold: [1, 0.85, 0] },
            )

            observer.observe(article)
        }
    })
}

const initiateArticleListAnimations = (list: HTMLDivElement, altScrollStart: number = 0) => {
    const articleCards = gsap.utils.toArray(list.querySelectorAll('.article-card'))

    let scrollStart = breakPointToScrollStartConverter[getWindowBreakPointForNewsroom()]
    if (altScrollStart) scrollStart = scrollStart + altScrollStart
    if (scrollStart > 100) scrollStart = 100

    articleCards.forEach((card) => {
        // @ts-ignore
        gsap.to(card, {
            y: -150,
            scrollTrigger: {
                start: `top ${scrollStart}%`,
                trigger: card,
                once: true,
            },
            ease: 'power1.inOut',
            duration: 1,
            opacity: 1,
            stagger: 0.25,
            onComplete() {
                if ((card as HTMLElement).dataset.videoId) initiateArticleVideo(card as HTMLElement);
                (card as HTMLElement).style.transition = '250ms ease-in-out'
            },
        })
    })
}

const initiateArticleSectionAnimation = (section: HTMLElement) => {
    const articleSections = gsap.utils.toArray(section)

    const scrollStart = breakPointToScrollStartConverter[getWindowBreakPointForNewsroom()] - 12

    articleSections.forEach(articleSection => {
        // @ts-ignore
        gsap.to(articleSection, {
            scrollTrigger: {
                start: `top ${scrollStart}%`,
                trigger: articleSection,
                once: true,
            },
            y: -150,
            opacity: 1,
            ease: 'power1.inOut',
            duration: 1,
            stagger: 0.25,
            onComplete() {
                (articleSection as HTMLElement).style.transition = '250ms ease-in-out'
            },
        })
    })
}

let isFirstPlacementCall = true

export const handleArticlePlacement = () => {
    const articlesContainer = document.getElementById('newsroom-articles-container')

    if (! articlesContainer || ! (articlesContainer instanceof HTMLDivElement)) return

    let allArticles: ArticleDataInterface[] = [...getCachedArticles() || []]
    let allSections: ArticleSectionDataInterface[] = [...getCachedSections() || []]

    if ((! allArticles.length || ! allSections.length) && isFirstPlacementCall) {
        const [cacheArticles, cacheSections] = allNewsroomData()

        setCachedArticles(cacheArticles)
        setCachedSections(cacheSections)

        allArticles = [...cacheArticles]
        allSections = [...cacheSections]

        isFirstPlacementCall = false
    }

    // @ts-ignore
    const columnsCount = breakPointToColumnCountConverter[getWindowBreakPointForNewsroom()]

    let listIndex = 1
    let specialBlocksPlaced = {
        'stayInformed': false,
        'anyQuestions': false,
        'events': false,
        'filters': false,
    }

    // Comment out the entire if section and the article sections are ignored
    if (allSections.length > 0) {
        allSections.forEach((section, indexSections: number) => {
            const articlesCount = section.displayAfter === 0 ? 16 : section.displayAfter

            if (! allArticles || allArticles.length < 1) return

            const articlesSplice = allArticles.splice(0, articlesCount)

            const columns = placeArticleLanes(columnsCount, articlesContainer, listIndex)
            const actualColumnsCount = columnsCount - 1
            let columnIndex = 0

            articlesSplice.forEach((article, indexArticles: number) => {
                columns[columnIndex].appendChild(makeElementFromString(articleHtml(article)))

                columnIndex++

                if (columnIndex > actualColumnsCount) columnIndex = 0

                if (! specialBlocksPlaced.anyQuestions && indexSections === 0 && anyQuestionsCard && indexArticles === SpecialCardPositions.ANY_QUESTIONS) {
                    columns[columnIndex].appendChild(anyQuestionsCard)

                    columnIndex++

                    if (columnIndex > actualColumnsCount) columnIndex = 0

                    specialBlocksPlaced.anyQuestions = true
                }

                if (! specialBlocksPlaced.events && indexSections === 0 && eventsCard && indexArticles === SpecialCardPositions.EVENTS) {
                    columns[columnIndex].appendChild(eventsCard)

                    columnIndex++

                    if (columnIndex > actualColumnsCount) columnIndex = 0

                    specialBlocksPlaced.events = true
                }

                if (! specialBlocksPlaced.filters && indexSections === 0 && filtersCard && indexArticles === SpecialCardPositions.FILTER_NEWSROOM) {
                    columns[columnIndex].appendChild(filtersCard)

                    columnIndex++

                    if (columnIndex > actualColumnsCount) columnIndex = 0

                    specialBlocksPlaced.filters = true
                }
            })

            listIndex++

            placeArticleSection(section, articlesContainer, listIndex)
        })
    }

    const columns = placeArticleLanes(columnsCount, articlesContainer, listIndex)
    const actualColumnsCount = columnsCount - 1
    let columnIndex = 0

    allArticles.forEach((article, indexArticles: number) => {
        columns[columnIndex].appendChild(makeElementFromString(articleHtml(article)))

        columnIndex++

        if (columnIndex > actualColumnsCount) columnIndex = 0

        if (! specialBlocksPlaced.anyQuestions && anyQuestionsCard && indexArticles === SpecialCardPositions.ANY_QUESTIONS) {
            columns[columnIndex].appendChild(anyQuestionsCard)

            columnIndex++

            if (columnIndex > actualColumnsCount) columnIndex = 0

            specialBlocksPlaced.anyQuestions = true
        }

        if (! specialBlocksPlaced.events && eventsCard && indexArticles === SpecialCardPositions.EVENTS) {
            columns[columnIndex].appendChild(eventsCard)

            columnIndex++

            if (columnIndex > actualColumnsCount) columnIndex = 0

            specialBlocksPlaced.events = true
        }

        if (! specialBlocksPlaced.filters && filtersCard && indexArticles === SpecialCardPositions.FILTER_NEWSROOM) {
            columns[columnIndex].appendChild(filtersCard)

            columnIndex++

            if (columnIndex > actualColumnsCount) columnIndex = 0

            specialBlocksPlaced.filters = true
        }
    })

    const articles = document.querySelectorAll('.article-card')

    articles.forEach((article) => {
        if (! (article instanceof HTMLElement)) return

        handleArticleClaps(article)
    })

    const firstList = document.querySelector('[data-list-index="1"]')
    const firstSection = document.querySelector('[data-next-list="2"]')

    if (firstList && firstList instanceof HTMLDivElement) initiateArticleListAnimations(firstList, 20)
    if (firstSection && firstSection instanceof HTMLElement) initiateArticleSectionAnimation(firstSection)
}

const breakPointToColumnCountConverter: { [bp in BreakPoint]: number } = {
    'XS': 2,
    'MIN': 2,
    'SM': 2,
    'MD': 3,
    'LG': 3,
    'XL': 4,
    'XXL': 4,
    'XXXL': 5,
}

const breakPointToScrollStartConverter = {
    'XS': 90,
    'MIN': 90,
    'SM': 90,
    'MD': 90,
    'LG': 90,
    'XL': 90,
    'XXL': 77,
    'XXXL': 77,
}

export const initNoResultsClearForm = () => {
    const resetButton = queryOne('#clear-field')!

    resetButton.addEventListener('click', () => noResultsSearchField.value = '')
}

export const addSearchTermToNoResultsContainer = (searchTerm: string) => {
    noResultsSearchField.value = searchTerm
    noResultsHeader.innerHTML = searchTerm
}

export const prettyPrintLabel = (value: string): string => {
    if (! value) {
        return ''
    }
    return value.toLowerCase()
        .replace('quick-filter', '')
        .replace(/[^a-z0-9,\s]/gi, ' ')
        .replace(/\s\s+/g, ' ')
        .split(',')
        .join(', ')
        .trim()
}

export const regenerateArticlesListAfterResize = async (): Promise<void> => {
    const articlesContainer = document.getElementById('newsroom-articles-container')

    if (! articlesContainer) return Promise.resolve()

    articlesContainer.innerHTML = ''

    await animateScrollTo(0, {
        maxDuration: 400,
    }).catch((error) => console.log(error))

    await new Promise((resolve) => requestAnimationFrame(resolve))

    handleArticlePlacement()
    autoSearch()
}
